<script lang="ts" setup>
import {
  ref,
  useExploreX,
  useRuntimeConfig,
  useTemplateRef,
  watch,
  usePlaces,
  callOnce,
  useSmartQuery,
  useSmartQueryArray,
  computed,
  useCategories,
  useCreators,
  useHeaderStyle,
  useRoute,
  useRouter,
  useSpots,
  useMaps,
  useMetrics,
} from "#imports";
import { onClickOutside } from "@vueuse/core";
import MPButton from "~/components/forms/MPButton.vue";
import MPPlaceSearch from "~/components/forms/MPPlaceSearch.vue";
import MPSelect from "~/components/forms/MPSelect.vue";
import MPSpotCardV2 from "~/components/layouts/MPSpotCardV2.vue";
import MPViewportSpill from "~/components/layouts/MPViewportSpill.vue";
import MPCreatorTag from "~/components/widgets/MPCreatorTag.vue";
import { useNagScreens } from "./explore/_useNagScreens";
import { useExploreMap } from "./explore/_useExploreMap";
import type { Place } from "~/stores/places";
import type { Spot } from "~/stores/spots";
import "mapbox-gl/dist/mapbox-gl.css";

// stores
const placesStore = usePlaces();
const exploreStore = useExploreX();
const categoryStore = useCategories();
const creatorStore = useCreators();
const spotStore = useSpots();
const mapStore = useMaps();
const placeStore = usePlaces();

// other setup
useHeaderStyle("opaque");
const config = useRuntimeConfig();
const route = useRoute();
const router = useRouter();
const metrics = useMetrics();
const mapElement = useTemplateRef("mapElement");

// query data
const country = useSmartQuery("country", true);
const region = useSmartQuery("region", true);
const place = useSmartQuery("place", true);
const categories = useSmartQueryArray("categories", false);
const creator = useSmartQuery("creator", false);

// countries and regions are already loaded, need to make sure we load the place if we are looking at one
await callOnce(
  async () => {
    // reset the explore store
    exploreStore.reset();

    await categoryStore.fetchCategories(null);
    await placeStore.fetchCountries(null);
    await placeStore.fetchRegions(null);

    // load featured map spots for creators
    const featuredMaps = await mapStore.fetchFeaturedMaps({ limit: 20 });
    await creatorStore.fetchByIds(featuredMaps.value.map((map) => map.user_id));
    const spots = await spotStore.fetchByIds(
      featuredMaps.value.map((fm) => fm.spotIds[0]!),
    );
    await placesStore.fetchByIds(
      spots.value.map((s) => s.place_id).filter((id) => typeof id === "string"),
    );

    if (place.value && region.value && country.value) {
      // if querystring place, fetch it as it won't be loaded by the map
      await placesStore.fetchPlace({
        country: country.value,
        region: region.value,
        place: place.value,
      });
    }
    if (typeof route.params.spotSlug === "string") {
      // direct link to spot
      await spotStore.fetchBySlugOrId(route.params.spotSlug);
    }
    if (creator.value) {
      // creator filter by default
      await creatorStore.fetchCreatorByHandle(creator.value);
    }
  },
  { mode: "navigation" },
);

// categories
const selectedCategories = computed(
  () => categoryStore.getByIds(exploreStore.currentCategories).value,
);
const selectedCategoryNames = computed(() => {
  const MAX_CATS = 2;
  let more: string = "";
  if (selectedCategories.value.length > MAX_CATS) {
    more = " (+ " + (selectedCategories.value.length - MAX_CATS) + " more)";
  }
  return (
    selectedCategories.value
      .slice(0, MAX_CATS)
      .map((sc) => sc.name)
      .join(", ") + more
  );
});

watch(
  categories,
  () =>
    exploreStore.setCategories(
      categoryStore
        .findCategoriesFromSlugs(categories.value)
        .value.map((c) => c.id),
    ),
  { immediate: true },
);

// creators
const selectedCreator = computed(() =>
  creator.value ? creatorStore.getCreatorByHandle(creator.value).value : null,
);

const creatorIdToSpot = (creatorId: string) => {
  const creator = creatorStore.getById(creatorId).value;
  if (!creator) return;
  const map = mapStore.getById(creator.mapIds[0]).value;
  if (!map) return;
  return spotStore.getById(map.spotIds[0]).value;
};

const featuredCreatorWithSpot = computed(() => {
  const creatorIds = mapStore.featuredMaps?.map((m) => m.user_id);
  if (!creatorIds) return [];

  return creatorIds
    ?.map((creatorId) => {
      const spot = creatorIdToSpot(creatorId);
      return {
        ...creatorStore.getById(creatorId).value!,
        spot: {
          ...spot,
          place: placesStore.getById(spot?.place_id).value!,
        },
      };
    })
    .filter((withSpot) => withSpot.spot);
});

const isFeaturedCreator = computed(
  () => exploreStore.visibleCreators.length === 0,
);
const creatorOptions = computed(() => {
  if (exploreStore.visibleCreators.length) {
    return exploreStore.visibleCreators;
  }
  return featuredCreatorWithSpot.value;
});

watch(
  creator,
  async () => {
    exploreStore.setCreator(selectedCreator.value?.id ?? null);
    if (!isFeaturedCreator.value || !selectedCreator.value) return;

    // if we selected a featured creator, zoom to the place of their first map spot
    const creatorWithSpot = featuredCreatorWithSpot.value.find(
      (c) => c.id === selectedCreator.value!.id,
    )!;
    country.value = creatorWithSpot.spot.place.country;
    region.value = creatorWithSpot.spot.place.region;
    place.value = creatorWithSpot.spot.place.place;
  },
  { immediate: true },
);

// MPPlaceSearch setting query string and all the associated magic
const inputPlace = ref<Place | null>(null);
watch(inputPlace, () => {
  if (inputPlace.value === null) {
    // input cleared
    place.value = region.value = country.value = null;
  } else {
    place.value = inputPlace.value.place;
    region.value = inputPlace.value.region;
    country.value = inputPlace.value.country;
  }
});

// get current place from query data
const currentPlace = computed(() => {
  if (!country.value) return null;
  return placesStore.getByCountryRegionPlace({
    country: country.value,
    region: region.value,
    place: place.value,
  }).value;
});

watch(currentPlace, () => {
  // if current place changes, update the input place
  inputPlace.value = currentPlace.value;
});

// filters
const showFilters = ref<boolean>(false);
const filterHeader = useTemplateRef("filterHeader");
onClickOutside(filterHeader, () => {
  showFilters.value = false;
});

// map setup and general utilities
const currentSpot = computed(() => {
  if (typeof route.params.spotSlug === "string") {
    return spotStore.getBySlug(route.params.spotSlug).value ?? null;
  }
  return null;
});

const showPlace = (newPlace: Place | null) => {
  if (newPlace) {
    country.value = newPlace.country;
    region.value = newPlace.region;
    place.value = newPlace.place;
  } else {
    country.value = region.value = place.value = null;
  }
};

const spotsVisible = ref<boolean>(true);
const resultsWrapper = useTemplateRef("resultsWrapper");
const showSpot = async (spot: Spot) => {
  spotsVisible.value = true;

  if (resultsWrapper.value?.content) {
    resultsWrapper.value.content.scrollLeft = 0; // reset viewport spill
  }
  router.push({ path: `/explore/${spot.current_slug}` });
};

const closeSpot = () => {
  router.push({ path: `/explore/` });
};

const { map, isMapReady } = useExploreMap({
  mapElement,
  config,
  currentPlace,
  currentSpot,
  showPlace,
  showSpot,
});

watch(isMapReady, () => {
  if (!isMapReady.value) return;
  useNagScreens({ map, metrics });
});

// spot list setup
const spots = computed(() =>
  exploreStore.visibleSpots.map((spot) => ({
    ...spot,
    place: placesStore.getById(spot.place_id).value!,
  })),
);

const spotsByDistance = computed(() => {
  if (!exploreStore.currentLatLng) return [];

  // find the centre of the screen
  const currentLat =
    (exploreStore.currentLatLng.max.lat - exploreStore.currentLatLng.min.lat) /
      2 +
    exploreStore.currentLatLng.min.lat;
  const currentLng =
    (exploreStore.currentLatLng.max.lng - exploreStore.currentLatLng.min.lng) /
      2 +
    exploreStore.currentLatLng.min.lng;

  // sorts in place, so copy and take the first 100
  const sortedSpots = [...spots.value].slice(0, 100);
  sortedSpots.sort((a, b) => {
    // pythagoras!
    const distanceA = Math.sqrt(
      Math.pow(currentLat - a.lat, 2) + Math.pow(currentLng - a.lon, 2),
    );
    const distanceB = Math.sqrt(
      Math.pow(currentLat - b.lat, 2) + Math.pow(currentLng - b.lon, 2),
    );
    return distanceA - distanceB;
  });

  return sortedSpots.slice(0, 50); // rendering only 50 keeps things performant
});

watch(spotsByDistance, () => {
  // if the open spot is not one of the closest 3 spots, close it
  if (!isMapReady.value || !spotsByDistance.value[0]) return;
  if (
    route.params.spotSlug &&
    route.params.spotSlug !== spotsByDistance.value[0].current_slug &&
    route.params.spotSlug !== spotsByDistance.value[1]?.current_slug &&
    route.params.spotSlug !== spotsByDistance.value[2]?.current_slug
  ) {
    closeSpot();
  }
});
</script>

<template>
  <div id="explore-page">
    <header :class="{ showFilters }" ref="filterHeader">
      <form action="/newExplore" @submit.prevent="">
        <MPPlaceSearch
          name="place"
          v-model="inputPlace"
          placeholder="Choose a destination"
          withFilters
          @clickFilter="showFilters = !showFilters"
        />

        <div v-show="showFilters" class="filters">
          <MPSelect
            name="category"
            v-model="categories"
            isMultiple
            isClearable
            isSearchable
            :options="
              exploreStore.visibleCategories.map((cat) => ({
                label: cat?.name,
                value: cat?.url_slug ?? cat?.name,
              }))
            "
          >
            <template #displayValue>{{
              selectedCategoryNames || "Filter by category"
            }}</template>
          </MPSelect>

          <MPSelect
            name="creators"
            v-model="creator"
            isClearable
            isSearchable
            isUnsetOnRemove
            :options="
              creatorOptions.map((creator) => ({
                label: `${creator.mappr_handle} ${creator.display_name}`,
                value: creator.mappr_handle!,
              }))
            "
          >
            <template #displayValue>
              <div v-if="selectedCreator" class="select-creator-label">
                <img
                  :src="
                    selectedCreator.profile_image_url ??
                    '/media/images/placeholder-user.webp'
                  "
                  alt=""
                  width="20"
                  height="20"
                />
                @{{ selectedCreator.mappr_handle }}
              </div>
              <div v-else>Filter by creator</div>
            </template>
            <template #optionLabel="{ value }">
              <div class="select-creator-label">
                <img
                  :src="
                    creatorStore.getCreatorByHandle(value).value
                      ?.profile_image_url ??
                    '/media/images/placeholder-user.webp'
                  "
                  alt=""
                  width="20"
                  height="20"
                />
                @{{
                  creatorStore.getCreatorByHandle(value).value?.mappr_handle
                }}
              </div>
            </template>
          </MPSelect>
        </div>
      </form>
    </header>

    <div id="map">
      <div
        ref="mapElement"
        :class="{ 'map-container': true, 'is-ready': isMapReady }"
      ></div>
    </div>

    <div class="results" v-show="spots.length > 0">
      <div class="result-actions">
        <MPButton
          :label="spotsVisible ? 'Hide results' : 'Show results'"
          :icon="
            spotsVisible
              ? 'ri-arrow-down-double-line'
              : 'ri-arrow-up-double-line'
          "
          version="secondary"
          size="small"
          @click="
            closeSpot();
            spotsVisible = !spotsVisible;
          "
        />
      </div>
      <div
        v-show="spotsVisible"
        :class="['spots', { 'item-open': currentSpot }]"
      >
        <MPViewportSpill
          ref="resultsWrapper"
          showControls
          controlsPosition="top"
          overflow="visible"
        >
          <TransitionGroup name="spots-list-animation" tag="ul">
            <li v-if="currentSpot" class="active" :key="currentSpot.id">
              <div class="spot-content">
                <NuxtPage @close="closeSpot" />
              </div>
            </li>
            <template v-for="spot in spotsByDistance" :key="spot.id">
              <li v-if="spot.id !== currentSpot?.id">
                <MPSpotCardV2
                  :coverImage="spot.cover_image_url!"
                  :isOpen="false"
                  @click="showSpot(spot)"
                >
                  <template #content>
                    <a class="spot-link">
                      <small>
                        {{ spot.place?.fullName }}
                      </small>
                      <h4 class="spot-name">
                        {{ spot.name }}
                      </h4>
                    </a>
                    <MPCreatorTag :creatorId="spot.creatorId" />
                  </template>
                </MPSpotCardV2>
              </li>
            </template>
          </TransitionGroup>
        </MPViewportSpill>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/scrollbar.pcss";
@import "@/assets/css/layouts.pcss";
@import "@/assets/css/animations.pcss";

#explore-page {
  header {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 100000;
    padding: 0.5rem 0.5rem 0.5rem 4rem;
    background: linear-gradient(
      180deg,
      #ffffff 40%,
      rgba(255, 255, 255, 0.48) 85%,
      rgba(255, 255, 255, 0) 100%
    );
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    @mixin screen md {
      padding: 0;
      position: absolute;
      top: 4.5rem;
      left: 50%;
      transform: translateX(-50%);
      width: fit-content;
      background-color: white;
      box-shadow: var(--shadow-base);
      border-radius: 2rem;
    }

    form {
      width: 100%;
    }

    &:deep(#place-search) {
      width: 100%;
      @mixin screen md {
        width: 25rem;
      }
    }

    &:deep(#place-search input) {
      border-radius: 2rem;
    }
    &:deep(#place-search .filter) {
      border-top-right-radius: 2rem;
      border-bottom-right-radius: 2rem;
    }

    &.showFilters {
      background: white;
      @mixin screen md {
        border-radius: 0.5rem;
      }
      &:deep(#place-search input) {
        border-radius: 0.5rem;
      }
      :deep(.select-trigger) {
        min-height: 3.3125rem;
      }
    }
  }
}

.filters {
  > :deep(div) {
    margin-top: 0.5rem;
  }
}

.select-creator-label {
  display: flex;
  align-items: center;

  > img {
    border-radius: 100%;
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.5rem;
  }
}

#map {
  position: fixed;
  top: 0;
  left: 0;
  height: 100dvh;
  width: 100vw;
  @mixin screen md {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
  }
}

.map-container {
  position: relative;
  visibility: hidden;
  height: 100%;
  overflow: hidden;

  &.is-ready {
    visibility: visible;
  }
}

.results {
  z-index: 1;
  position: absolute;
  bottom: 4.5rem;
  left: 0;
  width: 100%;
  min-height: 1.75rem;
  @mixin screen md {
    bottom: 3.5rem;
  }
}

.results ul {
  list-style: none;
  margin: 0;
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  padding: 0 0 0 0.75rem;
  @mixin screen md {
    padding-left: 1.5rem;
    padding-right: 75vw;
    gap: 1.5rem;
  }
}

.result-actions {
  z-index: 5;
  pointer-events: auto;
  position: absolute;
  top: -1.15rem;
  left: 0.75rem;
  @mixin screen md {
    left: 1.5rem;
  }
  &:deep(button) {
    padding: 0.65rem 0.75rem !important;
    color: var(--color-slate-500) !important;
    background-color: var(--color-slate-50) !important;
    border: 0 !important;
    box-shadow: var(--shadow-base);
    border-radius: 0.375rem !important;
    font-size: 0.95rem !important;
  }
}

.results .spots ul li {
  background-color: white;
  border-radius: 0.75rem;
  /*padding: 0.5rem;*/
  width: 18rem;
  height: fit-content;
  /*border: 1px solid var(--color-slate-200);*/
  box-shadow: var(--shadow-base);
  display: grid;
  @mixin screen md {
    width: 18rem;
    height: 35vh;
    max-height: 20rem;
  }

  &:hover {
    box-shadow: var(--shadow-xl);
    scale: 1.015;
    transition:
      scale 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out;
  }
}

.results,
.results ul,
.results .spots :deep(.swipe-content) {
  pointer-events: none;
}

.results ul li {
  pointer-events: auto;
}

$spotWidth: 25rem;
$spotHeight: 26rem;
.results .spots ul li.active {
  width: calc(100vw - 3rem);
  max-width: 30rem;
  position: relative;
  bottom: 0;
  left: 0;
  z-index: 9000;
  transition: width 0.3s ease-in-out;
  @mixin screen md {
    position: relative;
    width: $spotWidth;
    min-height: $spotHeight;
    overflow: hidden;
  }
}

.spot-content {
  background-color: black;
  width: calc(100vw - 3rem);
  max-width: 30rem;
  min-height: fit-content;
  height: 50vh;
  transition: all 0.3s ease-in-out;
  border-radius: 1rem;
  section {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
  @mixin screen md {
    background-color: black;
    position: absolute;
    top: 0;
    right: 0;
    width: $spotWidth;
    max-height: $spotHeight;
    transition: all 0.3s ease-in-out;
    overflow-y: scroll;
    display: flex;
    min-height: 100%;
    @mixin scrollbar-reset;
  }
}

.spot-name {
  line-clamp: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  overflow: hidden;
}

.content {
  position: absolute;
  z-index: 2;
  left: 2vw;
  bottom: -1rem;
  max-width: 30rem;
  max-height: 65vh;
  overflow-y: scroll;
  z-index: 9000;
  @mixin scrollbar-reset;
  border-radius: 1rem 1rem 0 0;
}

.spots-list-animation-move,
.spots-list-animation-enter-active,
.spots-list-animation-leave-active {
  transition: all 500ms ease;
}

.spots-list-animation-enter-from,
.spots-list-animation-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.spots-list-animation-leave-active {
  position: absolute;
}
</style>
